import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ref, onValue, set } from 'firebase/database';
import { database } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useCursor } from '../contexts/CursorContext';
import './FoggedMirror.css';

// Fogged Mirror - Steamy bathroom experience
const FoggedMirror = () => {
  const { userProfile } = useAuth();
  const { setPageOverride } = useCursor();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const mirrorRef = useRef(null);
  const [showMirror, setShowMirror] = useState(false);
  const [revealImage, setRevealImage] = useState(null);
  const [isWiping, setIsWiping] = useState(false);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [wipedAreas, setWipedAreas] = useState([]);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showUploadSection, setShowUploadSection] = useState(false);
  const [fogKey, setFogKey] = useState(0); // Increment to force fog re-initialization
  const regenIntervalRef = useRef(null);
  const fogInitializedRef = useRef(false);
  const lastWipedCountRef = useRef(0);
  const localWipedAreasRef = useRef([]); // Track wiped areas for regeneration targeting
  const fileInputRef = useRef(null); // Ref for file input to allow resetting

  // Steam particles
  const [steamParticles, setSteamParticles] = useState([]);

  // Override global custom cursor
  useEffect(() => {
    setPageOverride(true);
    return () => setPageOverride(false);
  }, [setPageOverride]);

  // Track fullscreen state
  useEffect(() => {
    const checkFullscreen = () => {
      const isInFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isInFullscreen);
    };

    checkFullscreen();

    document.addEventListener('fullscreenchange', checkFullscreen);
    document.addEventListener('webkitfullscreenchange', checkFullscreen);
    document.addEventListener('mozfullscreenchange', checkFullscreen);
    document.addEventListener('MSFullscreenChange', checkFullscreen);

    return () => {
      document.removeEventListener('fullscreenchange', checkFullscreen);
      document.removeEventListener('webkitfullscreenchange', checkFullscreen);
      document.removeEventListener('mozfullscreenchange', checkFullscreen);
      document.removeEventListener('MSFullscreenChange', checkFullscreen);
    };
  }, []);

  // Set upload mode flag for FullscreenHandler - allows exiting fullscreen to upload
  useEffect(() => {
    // Flag is true when in upload section - this gives grace period for file picker
    window.__intimacyUploadMode = showUploadSection;
    return () => {
      window.__intimacyUploadMode = false;
    };
  }, [showUploadSection]);

  // Generate steam particles
  useEffect(() => {
    const particles = [];
    for (let i = 0; i < 40; i++) {
      particles.push({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 5,
        duration: 5 + Math.random() * 5,
        size: 20 + Math.random() * 40
      });
    }
    setSteamParticles(particles);
  }, []);

  // Load reveal image from Firebase
  useEffect(() => {
    const imageRef = ref(database, 'intimacy/foggedMirror/revealImage');
    const unsubscribe = onValue(imageRef, (snapshot) => {
      if (snapshot.exists()) {
        setRevealImage(snapshot.val());
      }
    });
    return () => unsubscribe();
  }, []);

  // Load wiped areas from Firebase
  useEffect(() => {
    if (!showMirror) return;
    const wipedRef = ref(database, 'intimacy/foggedMirror/wipedAreas');
    const unsubscribe = onValue(wipedRef, (snapshot) => {
      if (snapshot.exists()) {
        setWipedAreas(snapshot.val());
      } else {
        setWipedAreas([]);
      }
    });
    return () => unsubscribe();
  }, [showMirror]);

  // Initialize fog canvas ONCE - draw the static fog pattern
  useEffect(() => {
    if (!showMirror || !canvasRef.current || !mirrorRef.current) return;
    if (fogInitializedRef.current) return; // Only initialize once

    const canvas = canvasRef.current;
    const mirror = mirrorRef.current;
    const ctx = canvas.getContext('2d');

    const rect = mirror.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    // Base fog - bluish gray, balanced opacity
    ctx.fillStyle = 'rgba(140, 160, 175, 0.95)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Add layered fog texture for depth
    for (let layer = 0; layer < 3; layer++) {
      const opacity = 0.20 - layer * 0.05;
      for (let i = 0; i < 100; i++) {
        const x = Math.random() * canvas.width;
        const y = Math.random() * canvas.height;
        const radius = 30 + Math.random() * 60;

        const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
        gradient.addColorStop(0, `rgba(180, 195, 210, ${opacity})`);
        gradient.addColorStop(1, 'rgba(180, 195, 210, 0)');

        ctx.fillStyle = gradient;
        ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
      }
    }

    // Add condensation droplets scattered across
    for (let i = 0; i < 300; i++) {
      const x = Math.random() * canvas.width;
      const y = Math.random() * canvas.height;
      const size = Math.random() * 4 + 1;

      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(200, 215, 230, ${Math.random() * 0.4 + 0.1})`;
      ctx.fill();
    }

    // Add vertical drip streaks like in reference
    for (let i = 0; i < 15; i++) {
      const x = Math.random() * canvas.width;
      const startY = Math.random() * canvas.height * 0.3;
      const length = 50 + Math.random() * 200;
      const width = 2 + Math.random() * 4;

      const dripGradient = ctx.createLinearGradient(x, startY, x, startY + length);
      dripGradient.addColorStop(0, 'rgba(100, 120, 140, 0.3)');
      dripGradient.addColorStop(0.5, 'rgba(80, 100, 120, 0.15)');
      dripGradient.addColorStop(1, 'rgba(80, 100, 120, 0)');

      ctx.beginPath();
      ctx.moveTo(x, startY);
      for (let y = startY; y < startY + length; y += 5) {
        const wobble = Math.sin((y - startY) * 0.05) * 2;
        ctx.lineTo(x + wobble, y);
      }
      ctx.strokeStyle = dripGradient;
      ctx.lineWidth = width;
      ctx.lineCap = 'round';
      ctx.stroke();
    }

    fogInitializedRef.current = true;
    lastWipedCountRef.current = 0;
  }, [showMirror, fogKey]);

  // Handle wipes from partner (remote sync) - only apply NEW wipes we haven't drawn locally
  useEffect(() => {
    if (!showMirror || !canvasRef.current || !fogInitializedRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Only process wipes beyond what we've drawn (synced from partner)
    const newWipes = wipedAreas.slice(lastWipedCountRef.current);

    newWipes.forEach(area => {
      const gradient = ctx.createRadialGradient(area.x, area.y, 0, area.x, area.y, area.radius);
      gradient.addColorStop(0, 'rgba(0, 0, 0, 1)');
      gradient.addColorStop(0.6, 'rgba(0, 0, 0, 0.9)');
      gradient.addColorStop(0.85, 'rgba(0, 0, 0, 0.4)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(area.x, area.y, area.radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalCompositeOperation = 'source-over';

      // Track for regeneration
      localWipedAreasRef.current.push({ ...area, timestamp: Date.now() });
    });

    lastWipedCountRef.current = wipedAreas.length;
  }, [showMirror, wipedAreas]);

  // Fog regeneration effect - wiped areas refog first, then edges
  useEffect(() => {
    if (!showMirror) return;

    // Delay start to ensure canvas is initialized
    const startDelay = setTimeout(() => {
      if (!canvasRef.current) return;

      regenIntervalRef.current = setInterval(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const width = canvas.width;
        const height = canvas.height;

        if (width === 0 || height === 0) return;

        const now = Date.now();
        const regenDelay = 2000; // 2 seconds before wiped areas start to refog
        const baseFogOpacity = 0.04; // Higher opacity for faster visible effect

        // FIRST: Refog previously wiped areas (after delay)
        localWipedAreasRef.current.forEach((area) => {
          const timeSinceWipe = now - area.timestamp;

          // Start refogging after delay
          if (timeSinceWipe > regenDelay) {
            // Gradually add fog back to this wiped area
            const regenProgress = Math.min((timeSinceWipe - regenDelay) / 3000, 1); // 3 seconds to fully refog
            const areaOpacity = baseFogOpacity * (1 + regenProgress * 4); // Stronger fog buildup

            const gradient = ctx.createRadialGradient(
              area.x, area.y, 0,
              area.x, area.y, area.radius * 1.3
            );
            gradient.addColorStop(0, `rgba(140, 160, 175, ${areaOpacity})`);
            gradient.addColorStop(0.6, `rgba(140, 160, 175, ${areaOpacity * 0.6})`);
            gradient.addColorStop(1, 'rgba(140, 160, 175, 0)');

            ctx.fillStyle = gradient;
            ctx.beginPath();
            ctx.arc(area.x, area.y, area.radius * 1.3, 0, Math.PI * 2);
            ctx.fill();
          }
        });

        // Clean up fully refogged areas (don't splice during iteration)
        localWipedAreasRef.current = localWipedAreasRef.current.filter(area => {
          const timeSinceWipe = now - area.timestamp;
          return timeSinceWipe <= regenDelay + 5000; // Keep for 5 seconds after regen starts
        });

        // SECOND: Add fog from edges
        const edgeDepth = 80;
        const edgeOpacity = 0.018; // Faster edge fog

        // Top edge
        const topGradient = ctx.createLinearGradient(0, 0, 0, edgeDepth);
        topGradient.addColorStop(0, `rgba(140, 160, 175, ${edgeOpacity})`);
        topGradient.addColorStop(1, 'rgba(140, 160, 175, 0)');
        ctx.fillStyle = topGradient;
        ctx.fillRect(0, 0, width, edgeDepth);

        // Bottom edge
        const bottomGradient = ctx.createLinearGradient(0, height, 0, height - edgeDepth);
        bottomGradient.addColorStop(0, `rgba(140, 160, 175, ${edgeOpacity})`);
        bottomGradient.addColorStop(1, 'rgba(140, 160, 175, 0)');
        ctx.fillStyle = bottomGradient;
        ctx.fillRect(0, height - edgeDepth, width, edgeDepth);

        // Left edge
        const leftGradient = ctx.createLinearGradient(0, 0, edgeDepth, 0);
        leftGradient.addColorStop(0, `rgba(140, 160, 175, ${edgeOpacity})`);
        leftGradient.addColorStop(1, 'rgba(140, 160, 175, 0)');
        ctx.fillStyle = leftGradient;
        ctx.fillRect(0, 0, edgeDepth, height);

        // Right edge
        const rightGradient = ctx.createLinearGradient(width, 0, width - edgeDepth, 0);
        rightGradient.addColorStop(0, `rgba(140, 160, 175, ${edgeOpacity})`);
        rightGradient.addColorStop(1, 'rgba(140, 160, 175, 0)');
        ctx.fillStyle = rightGradient;
        ctx.fillRect(width - edgeDepth, 0, edgeDepth, height);

      }, 100);
    }, 500);

    return () => {
      clearTimeout(startDelay);
      if (regenIntervalRef.current) {
        clearInterval(regenIntervalRef.current);
      }
    };
  }, [showMirror]);

  // Wipe function - creates realistic finger wipe marks
  const wipeArea = useCallback((x, y, radius = 45, save = true) => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Create realistic wipe with slightly rough edges
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
    gradient.addColorStop(0, 'rgba(0, 0, 0, 1)');
    gradient.addColorStop(0.6, 'rgba(0, 0, 0, 0.9)');
    gradient.addColorStop(0.85, 'rgba(0, 0, 0, 0.4)');
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');

    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';

    // Remove only directly overlapping tracked wipes (to reset their timers)
    // Use smaller threshold so only exact hits reset, not nearby areas
    const wipeDistance = radius * 0.5; // Only reset if wipe directly overlaps
    localWipedAreasRef.current = localWipedAreasRef.current.filter(area => {
      const dist = Math.sqrt(Math.pow(area.x - x, 2) + Math.pow(area.y - y, 2));
      return dist > wipeDistance; // Keep areas that aren't directly hit
    });

    // Add fresh entry with new timestamp (2-second timer resets)
    localWipedAreasRef.current.push({ x, y, radius, timestamp: Date.now() });

    if (save) {
      const newAreas = [...wipedAreas, { x, y, radius }];
      const limitedAreas = newAreas.slice(-500);
      // Increment counter BEFORE saving to prevent double-draw from sync effect
      lastWipedCountRef.current = limitedAreas.length;
      set(ref(database, 'intimacy/foggedMirror/wipedAreas'), limitedAreas);
    }
  }, [wipedAreas]);

  const handleWipeStart = (e) => {
    setIsWiping(true);
    handleWipe(e);
  };

  const handleWipeEnd = () => {
    setIsWiping(false);
  };

  const handleWipe = (e) => {
    if (!isWiping && e.type !== 'mousedown' && e.type !== 'touchstart') return;
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    let clientX, clientY;
    if (e.touches) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    const x = clientX - rect.left;
    const y = clientY - rect.top;

    wipeArea(x, y, 40);
  };

  const handleEnterBathroom = async () => {
    // Reset fog state before showing mirror
    await set(ref(database, 'intimacy/foggedMirror/wipedAreas'), []);
    setWipedAreas([]);
    lastWipedCountRef.current = 0;
    localWipedAreasRef.current = [];
    fogInitializedRef.current = false;
    setFogKey(prev => prev + 1);

    setShowMirror(true);
  };

  const handleBackToIntimacy = () => {
    navigate('/intimacy', { state: { returnToEros: true } });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      setUploadPreview(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSaveImage = async () => {
    if (!uploadPreview) return;
    setIsUploading(true);

    try {
      await set(ref(database, 'intimacy/foggedMirror/revealImage'), uploadPreview);
      setShowUploadSection(false);
      setUploadPreview(null);
      // Reset file input to clear cached selection
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Error saving image:', err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleResetFog = async () => {
    await set(ref(database, 'intimacy/foggedMirror/wipedAreas'), []);
    setWipedAreas([]);
    setShowResetConfirm(false);

    // Reset tracking refs
    lastWipedCountRef.current = 0;
    localWipedAreasRef.current = [];

    // Force fog re-initialization by changing the key
    fogInitializedRef.current = false;
    setFogKey(prev => prev + 1);
  };

  const requestFullscreen = () => {
    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen();
    } else if (elem.mozRequestFullScreen) {
      elem.mozRequestFullScreen();
    } else if (elem.msRequestFullscreen) {
      elem.msRequestFullscreen();
    }
  };

  // Intro Screen - with upload functionality
  if (!showMirror) {
    return (
      <div className="fogged-mirror-page fogged-mirror-intro">
        <div className="bathroom-background">
          <div className="bathroom-tiles" />
          <div className="bathroom-ambient" />
          {steamParticles.map(particle => (
            <div
              key={particle.id}
              className="steam-particle"
              style={{
                left: `${particle.left}%`,
                animationDelay: `${particle.delay}s`,
                animationDuration: `${particle.duration}s`,
                width: `${particle.size}px`,
                height: `${particle.size}px`
              }}
            />
          ))}
        </div>

        <motion.button
          className="fogged-back-button"
          onClick={handleBackToIntimacy}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          ← Back
        </motion.button>

        <div className="fogged-intro-container">
          <motion.div
            className="fogged-intro-content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
          >
            <motion.h1
              className="fogged-title"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              Steamy Secrets
            </motion.h1>

            {!showUploadSection ? (
              <>
                <motion.div
                  className="fogged-narrative"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8, duration: 1 }}
                >
                  <p className="narrative-line">The water runs hot...</p>
                  <motion.p
                    className="narrative-line"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.5 }}
                  >
                    Steam curls and rises, filling the room...
                  </motion.p>
                  <motion.p
                    className="narrative-line"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 2.5 }}
                  >
                    The mirror slowly disappears behind a veil of warmth...
                  </motion.p>
                </motion.div>

                <motion.div
                  className="intro-buttons"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 3.5, duration: 0.6 }}
                >
                  <motion.button
                    className="enter-bathroom-btn"
                    onClick={handleEnterBathroom}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Step to the Mirror
                  </motion.button>

                  <motion.button
                    className="set-image-btn"
                    onClick={() => setShowUploadSection(true)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Set Hidden Image
                  </motion.button>
                </motion.div>

                {revealImage && (
                  <motion.p
                    className="image-status"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 4 }}
                  >
                    An image awaits behind the steam...
                  </motion.p>
                )}
              </>
            ) : (
              <motion.div
                className="upload-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <p className="upload-instruction">
                  Choose an image to hide behind the fogged mirror
                </p>

                {isFullscreen && !uploadPreview && (
                  <p className="fullscreen-hint">
                    Press Escape or click below to exit fullscreen and select a file
                  </p>
                )}

                <div className="upload-area-intro">
                  {uploadPreview ? (
                    <div className="preview-container">
                      <img src={uploadPreview} alt="Preview" className="upload-preview-intro" />
                      <button
                        className="remove-preview-btn"
                        onClick={() => setUploadPreview(null)}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <label className="upload-label-intro">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        style={{ display: 'none' }}
                      />
                      <div className="upload-icon">+</div>
                      <span>Click to select image</span>
                    </label>
                  )}
                </div>

                {/* Re-enter fullscreen button after upload */}
                {!isFullscreen && uploadPreview && (
                  <motion.button
                    className="reenter-fullscreen-btn"
                    onClick={requestFullscreen}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    whileHover={{ scale: 1.02 }}
                  >
                    Re-enter Fullscreen to Save
                  </motion.button>
                )}

                <div className="upload-buttons">
                  <button
                    className="cancel-upload-btn"
                    onClick={() => {
                      setShowUploadSection(false);
                      setUploadPreview(null);
                      // Reset file input to clear cached selection
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="save-upload-btn"
                    onClick={handleSaveImage}
                    disabled={!uploadPreview || isUploading || !isFullscreen}
                  >
                    {isUploading ? 'Saving...' : !isFullscreen && uploadPreview ? 'Enter Fullscreen First' : 'Save Image'}
                  </button>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    );
  }

  // Main Bathroom Mirror Scene
  return (
    <div className="fogged-mirror-page fogged-mirror-main">
      <div className="bathroom-background">
        <div className="bathroom-tiles" />
        <div className="bathroom-ambient" />
        {steamParticles.map(particle => (
          <div
            key={particle.id}
            className="steam-particle"
            style={{
              left: `${particle.left}%`,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`,
              width: `${particle.size}px`,
              height: `${particle.size}px`
            }}
          />
        ))}
      </div>

      <motion.button
        className="fogged-back-button"
        onClick={() => setShowMirror(false)}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        ← Back
      </motion.button>

      <motion.button
        className="reset-fog-btn"
        onClick={() => setShowResetConfirm(true)}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        Reset Fog
      </motion.button>

      <motion.div
        className="mirror-container"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="mirror-frame">
          <div
            className="mirror-surface"
            ref={mirrorRef}
            onMouseDown={handleWipeStart}
            onMouseMove={handleWipe}
            onMouseUp={handleWipeEnd}
            onMouseLeave={handleWipeEnd}
            onTouchStart={handleWipeStart}
            onTouchMove={handleWipe}
            onTouchEnd={handleWipeEnd}
          >
            {/* Reveal image underneath */}
            {revealImage ? (
              <img
                src={revealImage}
                alt="Hidden"
                className="reveal-image"
              />
            ) : (
              <div className="no-image-placeholder">
                <span>No image set</span>
              </div>
            )}

            {/* Fog overlay canvas */}
            <canvas
              ref={canvasRef}
              className="fog-canvas"
            />
          </div>
        </div>
      </motion.div>

      <motion.p
        className="wipe-instruction"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        Wipe away the steam to reveal what's hidden...
      </motion.p>

      {/* Reset Confirmation Modal */}
      <AnimatePresence>
        {showResetConfirm && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowResetConfirm(false)}
          >
            <motion.div
              className="confirm-modal"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()}
            >
              <h2>Reset the Fog?</h2>
              <p>This will cover the mirror with fresh steam again.</p>

              <div className="modal-buttons">
                <button
                  className="cancel-btn"
                  onClick={() => setShowResetConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  className="confirm-btn"
                  onClick={handleResetFog}
                >
                  Reset Fog
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FoggedMirror;
